import Grid from 'Components/Atoms/Grid/Grid';
import Logo from 'Components/Atoms/Icons/Logo/Logo';
import Werkzoeken from 'Components/Atoms/Icons/Werkzoeken/Werkzoeken';
import Notification from 'Components/Atoms/Notification/Notification';
import Title from 'Components/Atoms/Title/Title';
import EditButton from 'Components/Molecules/EditButton/EditButton';
import FormGroup from 'Components/Molecules/Form/FormGroup/FormGroup';
import Field from 'Components/Organisms/Field/Field';
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import { updateBusinessUnit } from 'Store/BusinessUnit/Actions';
import { updateCandidate } from 'Store/Candidate/Actions';
import { updateContact } from 'Store/Contact/Actions';
import { FieldType } from 'Store/FieldManagement/PropTypes';
import { StoreState } from 'Store/Reducers';
import { getEntityById } from 'Store/Settings/Entities/Selectors';
import { fetchEntityTypesByEntityId } from 'Store/Settings/EntityTypes/Actions';
import { getEntityTypeById } from 'Store/Settings/EntityTypes/Selector';
import { getPublicFieldsByEntityIdAsArray } from 'Store/Settings/Fields/Selectors';
import { getUsedFieldsInLayoutsByEntityId } from 'Store/Settings/Layout/Selectors';
import { getRecordById, getValuesByEntityNameByEntityId, isEntityPatching } from 'Store/Shared/Store/Values/Selector';
import { updateVacancy } from 'Store/Vacancy/Actions';
import styles from './EntityPublications.module.sass';
import {getDatasetLabels} from 'Store/FieldManagement/Selectors';

export const notPublishable = [
    '499e0ec6-593d-4215-8aa4-f2cb133c78af', // Kandidaten - intern
    'c67f06fe-322b-4048-8fe2-5d5da2a0cc42'  // Vacatures - extern
];

const EntityPublications = ({
    entity_id, record_id,
    entity, record,
    datasetLabelsById, values, entityType,
    fields, fieldsUsedInLayouts,

    isEntityPatching,

    ...props
}: EntityPublicationsProps) => {
    const updateRecord = useMemo(() => EntityHelper.update(props, entity?.name), [entity?.name]);

    const slugFieldIds = useMemo(() => {
        if (entity?.name === 'candidate') {
            return [
                '085ef8fb-7248-4e5d-8016-2399b1477077',
                'ed29b10c-00bb-4037-9093-210ae215eb3b',
                '6db02aab-4215-4de6-8974-5abc8de12eb6'
            ];
        }

        if (entity?.name === 'vacancy') {
            return [
                '9673ba84-ff9b-474d-aef8-c7da7e4abf9c',
                'ba927377-4ee1-4b78-b0eb-288d85304f22',
                '3ee1736f-bed4-47aa-a5cc-6326fdd26a24'
            ]
        }

        return [];
    }, [entity?.name]);

    const [state, setState] = useState({
        isEdit: false
    });

    const [data, setData] = useState<any>([
        ...(record.is_published ? ['growing-people.nl'] : []),
        ...(record.is_sponsored ? ['werkzoeken.nl'] : [])
    ]);

    useEffect(() => {
        setData([
            ...(record.is_published ? ['growing-people.nl'] : []),
            ...(record.is_sponsored ? ['werkzoeken.nl'] : [])
        ]);
    }, [record]);

    const isValid = useMemo(() => {
        return fields
            ?.filter(i => fieldsUsedInLayouts?.includes(i?.id))
            ?.every(i => {
                const value = values?.[i?.id]?.value;

                if (Array.isArray(value) && value?.length > 0){
                    if (i.type === FieldType.Combination) {
                        const combinationFields = i.default?.fields;
                        return  combinationFields.every(i =>
                            Array.isArray(value?.[0]?.[i])
                            && value?.[0]?.[i]?.length > 0
                        );
                    }

                    return true;
                }

                if (typeof value === 'boolean'
                || typeof value === 'number'
                || typeof value === 'string') {
                    return true;
                }

                return false;
            })
    }, [values, fields, fieldsUsedInLayouts]);

    const list = useMemo(() => {
        let list: any = [];

        if (typeof record.is_published !== 'undefined') {
            list.push({
                label: 'growing-people.nl',
                value: 'growing-people.nl'
            });
        }

        if (typeof record.is_sponsored !== 'undefined') {
            list.push({
                label: 'werkzoeken.nl',
                value: 'werkzoeken.nl'
            });
        }

        return list;
    }, [record, isValid]);

    const handleChange = (value) => setData(value);

    const handleSave = () =>
        updateRecord(
            record_id,
            {
                is_published: data?.includes('growing-people.nl'),
                is_sponsored: data?.includes('werkzoeken.nl')
            }
        )

    const handleEdit = (value) => {
        if (!value) {
            setData([
                ...(record.is_published ? ['growing-people.nl'] : []),
                ...(record.is_sponsored ? ['werkzoeken.nl'] : [])
            ]);
        }

        setState((state) => ({
            ...state,
            isEdit: value
        }));
    };

    return <div className={styles['entitypublications']}>
        <Grid
            position='between'
            align='center'
            axis='horizontal'
        >
            <Title weight='semibold'>Publicatie</Title>
            <EditButton
                variant='button'
                isEdit={state.isEdit}
                onChange={handleEdit}
                onSave={handleSave}
                isLoading={isEntityPatching}
                isDisabled={(!record?.is_published && !record?.is_sponsored && !isValid) || notPublishable.includes(entityType?.id)}
            />
        </Grid>

        {
            notPublishable.includes(entityType?.id)
            ? <Notification variant='info'>
                Dit {EntityHelper.getSingularDisplayValueByName(entity.name)?.toLowerCase()} type kan niet worden gepubliceerd
            </Notification>
            : (record?.is_published || record?.is_sponsored || isValid)
            ? <div className={styles['entitypublications__published']}>
                <FormGroup title='Gepubliceerd op'>
                    {
                        !state.isEdit &&
                        <div className={styles['entitypublications__published-list']}>
                            {
                                typeof record?.is_published !== 'undefined' &&
                                <div
                                    style={{ cursor: record?.is_published ? 'pointer' : 'default' }}
                                    className={styles['entitypublications__published-list-item__state--'+(record?.is_published ? 'active' : 'inactive')]}
                                    onClick={() => {
                                        if (!record?.is_published) return;
                                        const slug = slugFieldIds.reduce(
                                            (acc, curr) =>
                                                acc += `${datasetLabelsById?.[values?.[curr]?.value?.toString()] ?? values?.[curr]?.value?.toString()}-`,
                                            ''
                                        );
                                        const entitySlug = entity.name === 'candidate' ? 'talenten' : 'vacatures';

                                        window.open(`https://www.growing-people.nl/${entitySlug}/${slug}${record.public_id}`?.toLowerCase(), '_blank');
                                    }}
                                >
                                    <Logo />
                                </div>
                            }
                            {
                                typeof record?.is_sponsored !== 'undefined' &&
                                <div className={styles['entitypublications__published-list-item__state--'+(record?.is_sponsored ? 'active' : 'inactive')]}>
                                    <Werkzoeken />
                                </div>
                            }
                        </div>
                    }
                    {
                        state.isEdit &&
                        <Field
                            isEdit={true}
                            value={data}
                            type={FieldType.MultiSelect}
                            onChange={handleChange}
                            properties={{
                                list
                            }}
                        />
                    }
                </FormGroup>
            </div>
            : <Notification variant='info'>
                Publicatie is pas mogelijk als alle openbare velden zijn ingevuld voor deze record.
            </Notification>
        }
    </div>;
}

interface Props {
    entity_id: any
    record_id: any
}

const mapState = (state: StoreState, props: Props) => ({
    entity: getEntityById(state, props.entity_id),
    record: getRecordById(state, props.record_id),

    fields: getPublicFieldsByEntityIdAsArray(state, props.entity_id),
    fieldsUsedInLayouts: getUsedFieldsInLayoutsByEntityId(state, props.entity_id),

    datasetLabelsById: getDatasetLabels(state),
    values: getValuesByEntityNameByEntityId(state, getEntityById(state, props.entity_id)?.name, props.record_id),
    entityType: getEntityTypeById(state, getRecordById(state, props.record_id)?.type_id),

    isEntityPatching: isEntityPatching(state, getEntityById(state, props.entity_id)?.name)
});

const mapDispatch = {
    updateVacancy,
    updateCandidate,
    updateBusinessUnit,
    updateContact,
    fetchEntityTypesByEntityId
}

type EntityPublicationsProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(EntityPublications);